import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';
import { BehaviorAnalysisDto, StudentBehaviorCardDto } from './BehaviorTypes';

function mapClasses(response: any) {
  const classesMap = new Map();
  response.meta.classes.forEach((item: any) => {
    classesMap.set(item.id, item);
  });
  response.data = response.data.map((item: any) => {
    item.class = classesMap.get(item.classId);
    return item;
  });
  return response;
}

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery,
  tagTypes: ['PaymentSummary'],
  endpoints: builder => ({
    getYearPayments: builder.query<
      any,
      { academicYearId?: string; sessionId?: string; fromDate?: string; toDate?: string }
    >({
      query: ({ academicYearId, sessionId, fromDate, toDate }) => {
        const params = new URLSearchParams();
        if (academicYearId) params.append('academic_year_id', academicYearId);
        if (sessionId) params.append('session_id', sessionId);
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
        return `/reports/payments/by-account?${params.toString()}`;
      },
      providesTags: ['PaymentSummary'],
    }),
    getSessionPayments: builder.query<
      any,
      { sessionId?: string; academicYearId?: string; fromDate?: string; toDate?: string }
    >({
      query: ({ sessionId, academicYearId, fromDate, toDate }) => {
        const params = new URLSearchParams();
        if (sessionId) params.append('session_id', sessionId);
        if (academicYearId) params.append('academic_year_id', academicYearId);
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
        return `/reports/payments/by-account?${params.toString()}`;
      },
      providesTags: ['PaymentSummary'],
    }),
    getDailyPayments: builder.query<
      any,
      {
        fromDate: string;
        toDate: string;
        classId: string;
        paymentMethodId: string;
        status?: string;
      }
    >({
      query: ({ fromDate, toDate, classId, paymentMethodId, status }) => {
        const params = new URLSearchParams();
        if (fromDate) params.append('fromDate', fromDate);
        if (toDate) params.append('toDate', toDate);
        if (classId) params.append('classId', classId);
        if (paymentMethodId) params.append('paymentMethodId', paymentMethodId);
        if (status) {
          params.append('status', status);
        }
        return `/reports/payments/log?${params.toString()}`;
      },
    }),
    getEnrollmentStats: builder.query({
      query: () => `/reports/enrollment/stats`,
    }),
    getDebtorsList: builder.query<
      any,
      { sessionId?: string; searchTerm?: string }
    >({
      query: ({ sessionId, searchTerm }) => {
        const params = new URLSearchParams();
        if (searchTerm) {
          params.append('amount', searchTerm);
        }
        if (sessionId) {
          params.append('sessionId', sessionId);
        }
        if (params.toString() === '') {
          return `/reports/debtors`;
        }
        return `/reports/debtors?${params.toString()}`;
      },
      transformResponse: mapClasses,
    }),
    getFullyPaidList: builder.query({
      query: () => `/reports/fully-paid`,
      transformResponse: mapClasses,
    }),
    getZeroPaymentList: builder.query({
      query: () => `/reports/zero-payment`,
      transformResponse: mapClasses,
    }),
    getBehaviorReport: builder.query<
      any,
      {
        startDate?: string;
        endDate?: string;
        period?: string;
        classId?: number;
        studentId?: number;
        behaviorType?: 'positive' | 'negative' | 'all';
        sessionId?: number;
        format?: 'html' | 'pdf' | 'json';
      }
    >({
      query: ({ format = 'json', ...params }) => {
        const searchParams = new URLSearchParams();
        if (params.startDate) {
          searchParams.append('start_date', params.startDate);
        }
        if (params.endDate) {
          searchParams.append('end_date', params.endDate);
        }
        if (params.period) {
          searchParams.append('period', params.period);
        }
        if (params.classId) {
          searchParams.append('class_id', params.classId.toString());
        }
        if (params.studentId) {
          searchParams.append('student_id', params.studentId.toString());
        }
        if (params.behaviorType) {
          searchParams.append('behavior_type', params.behaviorType);
        }
        if (params.sessionId) {
          searchParams.append('session_id', params.sessionId.toString());
        }

        const endpoint = format === 'json' 
          ? `/reports/v2/behavior-report` 
          : `/reports/v2/behavior-report/${format}`;
        
        return `${endpoint}?${searchParams.toString()}`;
      },
    }),
    getFeesOverviewReport: builder.query<
      any,
      {
        sessionId?: number;
        classId?: number;
        fromDate?: string;
        toDate?: string;
        format?: 'html' | 'pdf' | 'json';
      }
    >({
      query: ({ format = 'json', ...params }) => {
        const searchParams = new URLSearchParams();
        
        if (params.sessionId) {
          searchParams.append('session_id', params.sessionId.toString());
        }
        if (params.classId) {
          searchParams.append('class_id', params.classId.toString());
        }
        if (params.fromDate) {
          searchParams.append('from_date', params.fromDate);
        }
        if (params.toDate) {
          searchParams.append('to_date', params.toDate);
        }

        const endpoint =
          format === 'json'
            ? `/reports/v2/fees-overview`
            : `/reports/v2/fees-overview/${format}`;

        return `${endpoint}?${searchParams.toString()}`;
      },
    }),
    getStudentRankingsReport: builder.query<
      any,
      {
        period: string;
        classId?: number;
        limit?: number;
        format?: 'html' | 'pdf' | 'json';
      }
    >({
      query: ({ format = 'json', ...params }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('period', params.period);
        if (params.classId) {
          searchParams.append('class_id', params.classId.toString());
        }
        if (params.limit) {
          searchParams.append('limit', params.limit.toString());
        }

        const endpoint =
          format === 'json'
            ? `/reports/v2/student-rankings`
            : `/reports/v2/student-rankings/${format}`;

        return `${endpoint}?${searchParams.toString()}`;
      },
    }),
    getBehaviorTrendsReport: builder.query<
      BehaviorAnalysisDto,
      {
        startDate?: string;
        endDate?: string;
        classId?: number;
        format?: 'html' | 'pdf' | 'json';
      }
    >({
      query: ({ format = 'json', ...params }) => {
        const searchParams = new URLSearchParams();
        if (params.startDate) {
          searchParams.append('start_date', params.startDate);
        }
        if (params.endDate) {
          searchParams.append('end_date', params.endDate);
        }
        if (params.classId) {
          searchParams.append('class_id', params.classId.toString());
        }

        const endpoint =
          format === 'json'
            ? `/reports/v2/behavior-trends`
            : `/reports/v2/behavior-trends/${format}`;

        return `${endpoint}?${searchParams.toString()}`;
      },
    }),
    getStudentBehaviorCardReport: builder.query<
      StudentBehaviorCardDto,
      {
        studentId: number;
        format?: 'html' | 'pdf' | 'json';
      }
    >({
      query: ({ format = 'json', ...params }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('student_id', params.studentId.toString());

        const endpoint =
          format === 'json'
            ? `/reports/v2/student-behavior-card`
            : `/reports/v2/student-behavior-card/${format}`;

        return `${endpoint}?${searchParams.toString()}`;
      },
    }),
    getAttendanceReport: builder.query<
      any,
      {
        sessionId?: number;
        classIds?: string;
        format?: 'html' | 'pdf' | 'json' | 'csv';
      }
    >({
      query: ({ format = 'json', ...params }) => {
        const searchParams = new URLSearchParams();
        if (params.sessionId) {
          searchParams.append('session_id', params.sessionId.toString());
        }
        if (params.classIds) {
          searchParams.append('class_ids', params.classIds);
        }

        const endpoint =
          format === 'json'
            ? `/reports/v2/attendance-statistics`
            : `/reports/v2/attendance-statistics/${format}`;

        return `${endpoint}?${searchParams.toString()}`;
      },
    }),
  }),
});

export const {
  useGetYearPaymentsQuery,
  useGetSessionPaymentsQuery,
  useGetDailyPaymentsQuery,
  useGetEnrollmentStatsQuery,
  useGetDebtorsListQuery,
  useGetFullyPaidListQuery,
  useGetZeroPaymentListQuery,
  useGetBehaviorReportQuery,
  useGetFeesOverviewReportQuery,
  useGetStudentRankingsReportQuery,
  useGetBehaviorTrendsReportQuery,
  useGetStudentBehaviorCardReportQuery,
  useGetAttendanceReportQuery,
} = reportsApi;
